// Zod parse helper for the trust-boundary surface.
//
// **`parseOrThrow`** wraps `schema.safeParse(value)` and throws with a
// caller-supplied context prefix on failure. Used at the render seam
// (D-079, render-block.ts) where a validation failure represents a
// bug or stale persisted state — the caller wants an exception, not
// a result-typed branch.
//
// **Why a single-export module.** Originally co-located with
// `formatFirstZodIssue` (D-096) which served the P-034 JSON import
// boundary; the JSON import/export pair was dropped at P-037.31 §C
// (V1 archival mechanism superseded by V2's planned cloud sync), so
// `formatFirstZodIssue` dropped to zero product consumers and was
// removed alongside `src/lib/json-import.ts`. `parseOrThrow` keeps
// one consumer (`render-block.ts`) and stays here as the canonical
// Zod-throws-on-failure helper.
//
// **Error formatting.** Renders the first Zod issue as
// `path.dot.path: message` (or `<root>: message` for top-level errors).
// Falls back to `error.message` only when the issues array is empty.

import type { z } from 'zod'

export function parseOrThrow<T>(schema: z.ZodType<T>, value: unknown, context: string): T {
  const result = schema.safeParse(value)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const formatted =
    issue === undefined
      ? result.error.message
      : `${issue.path.length > 0 ? issue.path.join('.') : '<root>'}: ${issue.message}`
  throw new Error(`${context}: ${formatted}`)
}
